import React, {useEffect, useRef, useState} from "react";
import ReactDOM from 'react-dom';
import {toast} from "react-toastify";
import {t} from "i18next";
import {ArrowRight, Check, ChevronDown, Loader2, Search, ShieldCheck, UserSearch, X} from "lucide-react";
import {familyTreeNames} from "../data/FamilyTreeNames";
import {useAuth} from "../hooks/useAuth";
import {
	findProfiles,
	sendProfileClaimOtp,
	submitProfileClaimRequest,
	verifyProfileClaimOtp,
} from "../service/accountApi";
import {countryCodes} from "../data/CountryCode";
import {ChevronDownIcon} from "@radix-ui/react-icons";


// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const getScrollbarWidth = (el) => el.offsetWidth - el.clientWidth;

const useDelayedClose = (isOpen, delay = 140) => {
	const [shouldRender, setShouldRender] = useState(isOpen);
	const [isClosing, setIsClosing] = useState(false);
	useEffect(() => {
		if (isOpen) {
			setShouldRender(true);
			setIsClosing(false);
		} else if (shouldRender) {
			setIsClosing(true);
			const t = setTimeout(() => {
				setShouldRender(false);
				setIsClosing(false);
			}, delay);
			return () => clearTimeout(t);
		}
	}, [isOpen]);
	return {shouldRender, isClosing};
};

const DEFAULT_COUNTRY = countryCodes.find(c => c.countryCode === 'IN') ?? countryCodes[0];
const makePhoneState = (country = DEFAULT_COUNTRY) => ({
	charCode: country.countryCode,
	dialingCode: country.countryDialingCode,
	countryFlagSrc: country.countryFlagSrc,
	countryName: country.countryName,
	number: '',
});

const STEPS = {SEARCH: "SEARCH", RESULTS: "RESULTS", OTP: "OTP", CLAIM: "CLAIM", SUCCESS: "SUCCESS"};
const STEP_ORDER = [STEPS.SEARCH, STEPS.RESULTS, STEPS.OTP, STEPS.CLAIM];
const RESEND_COOLDOWN = 30;

// i18n shorthand
const tf = (key, opts) => t(`FIND_MY_PROFILE.${key}`, opts);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES  (mirrors YellowPageOnboardingForm patterns exactly)
// ─────────────────────────────────────────────────────────────────────────────

const Field = ({children, error}) => (
	<div className="flex flex-col gap-1">
		{children}
		{error && (
			<p className="flex items-center gap-1 font-custom-medium text-[0.68rem] text-red-500 mt-0.5">
				<span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block shrink-0"/>
				{error}
			</p>
		)}
	</div>
);

const InputLabel = ({htmlFor, children, required}) => (
	<label htmlFor={htmlFor} className="font-custom-semi-bold text-[0.75rem] text-gray-600 tracking-wide">
		{children}
		{required && <span className="text-red-400 ml-0.5">*</span>}
	</label>
);

const inputCls = (hasError) =>
	`w-full px-3.5 py-2.5 text-sm rounded-xl border outline-none transition-all duration-200 
    font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
    ${hasError
		? "border-red-300 ring-2 ring-red-100 focus:ring-red-200"
		: "border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10 hover:border-gray-300"
	}`;

// ─────────────────────────────────────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────────────────────────────────────

const STEP_LABELS = {
	[STEPS.SEARCH]: "Search",
	[STEPS.RESULTS]: "Results",
	[STEPS.OTP]: "Verify",
	[STEPS.CLAIM]: "Request",
};

const StepIndicator = ({currentStep}) => {
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	return (
		<div className="flex items-center w-full mt-3">
			{STEP_ORDER.map((step, i) => {
				const done = i < currentIndex;
				const active = i === currentIndex;
				return (
					<React.Fragment key={step}>
						<div className="flex flex-col items-center gap-1">
							<div
								className="w-8 h-8 rounded-full flex items-center justify-center font-custom-bold text-xs transition-all duration-300"
								style={{
									background: done ? "#16A34A" : active ? "#FDF0ED" : "#F3F4F6",
									border: `2px solid ${done ? "#16A34A" : active ? "#D26A53" : "#E5E7EB"}`,
									color: done ? "#fff" : active ? "#D26A53" : "#9CA3AF",
									boxShadow: done
										? "0 0 0 3px rgba(22,163,74,0.12)"
										: active
											? "0 0 0 3px rgba(210,106,83,0.10)"
											: "none",
								}}
							>
								{done ? <Check className="w-3 h-3"/> : i + 1}
							</div>
							<span
								className="font-custom-semi-bold text-[0.55rem] tracking-wide"
								style={{color: active ? "#D26A53" : done ? "#6B7280" : "#9CA3AF"}}
							>
                                {STEP_LABELS[step]}
                            </span>
						</div>
						{i < STEP_ORDER.length - 1 && (
							<div className="flex-1 mx-1.5 mb-4">
								<div className="h-0.5 w-full rounded-full overflow-hidden bg-gray-200">
									<div
										className="h-full rounded-full transition-all duration-500"
										style={{
											width: i < currentIndex ? "100%" : "0%",
											background: "#16A34A",
										}}
									/>
								</div>
							</div>
						)}
					</React.Fragment>
				);
			})}
		</div>
	);
};
// ─────────────────────────────────────────────────────────────────────────────
// PROFILE AVATAR
// ─────────────────────────────────────────────────────────────────────────────

const ProfileAvatar = ({profile, size = "h-11 w-11"}) => {
	const hasRealPhoto = profile.imageUrl && profile.imageUrl.length > 2;
	const initials = profile.imageUrl?.length <= 2
		? profile.imageUrl.toUpperCase()
		: `${profile.firstName?.[0] ?? ""}${profile.middleName?.[0] ?? ""}`.toUpperCase();
	const bgColor = profile.imageUrlBg
		? profile.imageUrlBg.replace("bg-[", "").replace("]", "")
		: "#D26A53";
	return (
		<div
			className={`${size} shrink-0 rounded-full flex items-center justify-center
                text-white font-custom-mono-bold text-sm shadow-sm overflow-hidden`}
			style={hasRealPhoto ? {} : {backgroundColor: bgColor}}
		>
			{hasRealPhoto
				? <img src={profile.imageUrl} alt={profile.firstName}
				       className="w-full h-full object-cover" referrerPolicy="no-referrer"/>
				: <span>{initials || "?"}</span>
			}
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE CARD
// ─────────────────────────────────────────────────────────────────────────────

const ProfileCard = ({profile, onSelect}) => {
	const isLocked = profile.isAlreadyRegistered;
	const [hovered, setHovered] = useState(false);

	return (
		<button
			type="button"
			disabled={isLocked}
			onClick={isLocked ? undefined : () => onSelect(profile)}
			onMouseEnter={() => !isLocked && setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className="w-full text-left flex items-center gap-3 p-3.5 rounded-xl transition-all duration-200"
			style={{
				border: `2px solid ${hovered ? "#D26A53" : "#E5E7EB"}`,
				background: hovered ? "#FDF5F3" : isLocked ? "#FAFAFA" : "#FDFDFD",
				boxShadow: hovered ? "0 2px 12px rgba(210,106,83,0.12)" : "none",
				cursor: isLocked ? "not-allowed" : "pointer",
				opacity: isLocked ? 0.65 : 1,
			}}
		>
			<ProfileAvatar profile={profile}/>
			<div className="flex-1 min-w-0">
				<p className="font-custom-bold text-sm text-gray-900 truncate">
					{[profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ")}
				</p>
				<p className="font-custom-medium text-[0.68rem] text-gray-500 truncate mt-0.5">
					{[
						profile.familyTreeName,
						profile.generation ? `${tf("RESULTS.GENERATION")} ${profile.generation}` : null,
						profile.ageRange ? `${tf("RESULTS.AGE")} ${profile.ageRange}` : null,
						profile.city ? profile.city : null,
					].filter(Boolean).join(" · ")}
				</p>
				{(profile.pageNumber || profile.membershipNumber) && (
					<p className="font-custom-regular text-[0.6rem] text-gray-400 mt-0.5">
						{[
							profile.pageNumber ? `${tf("RESULTS.PAGE")} ${profile.pageNumber}` : null,
							profile.membershipNumber ? `Memb. #${profile.membershipNumber}` : null,
						].filter(Boolean).join(" · ")}
					</p>
				)}
			</div>
			{isLocked ? (
				<span className="font-custom-bold text-[0.6rem] text-amber-600 bg-amber-50
                    border border-amber-200 px-2 py-1 rounded-full shrink-0 leading-tight">
                    {tf("RESULTS.ALREADY_REGISTERED_BADGE")}
                </span>
			) : (
				<div
					className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200"
					style={{background: hovered ? "#D26A53" : "#F3F4F6"}}
				>
					<ArrowRight
						className="w-3.5 h-3.5 transition-colors duration-200"
						style={{color: hovered ? "#fff" : "#9CA3AF"}}
					/>
				</div>
			)}
		</button>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// PhoneRow — ④ keyboard-navigable country-code dropdown
// ─────────────────────────────────────────────────────────────────────────────
const PhoneRow = ({
	                  value,
	                  onChange,
	                  countryCode,
	                  onCountryChange,
	                  error,
	                  placeholder = "9320638137",
	                  allCountryCodes
                  }) => {
	const [open, setOpen] = useState(false);
	const {shouldRender: showDrop, isClosing: dropClosing} = useDelayedClose(open);
	const [dropPos, setDropPos] = useState({top: 0, left: 0, width: 0});
	const [focusedIdx, setFocusedIdx] = useState(-1);
	const listRef = useRef(null);
	const btnRef = useRef(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const scrollParent = btnRef.current?.closest('.overflow-y-auto');
		if (!scrollParent) return;
		if (open) {
			const sbWidth = getScrollbarWidth(scrollParent);
			const existingPadding = parseFloat(getComputedStyle(scrollParent).paddingRight) || 0;
			scrollParent.style.overflow = 'hidden';
			scrollParent.style.paddingRight = `${existingPadding + sbWidth}px`;
		} else {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		}
		return () => {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		};
	}, [open]);

	useEffect(() => {
		if (open && btnRef.current) {
			const r = btnRef.current.getBoundingClientRect();
			setDropPos({top: r.bottom + 4, left: r.left, width: r.width * 3});
			// width * 3 approximates the min-w-[11.25rem]; adjust to taste
		}
	}, [open]);

	useEffect(() => {
		if (focusedIdx >= 0 && listRef.current)
			listRef.current.children[focusedIdx]?.scrollIntoView({block: 'nearest'});
	}, [focusedIdx]);

	useEffect(() => {
		const handler = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				closeDropdown();
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [open]);

	// ✅ CHANGED: returnFocus param — only Escape returns focus, Tab does not
	const closeDropdown = (returnFocus = false) => {
		setOpen(false);
		setFocusedIdx(-1);
		if (returnFocus) btnRef.current?.focus();
	};

	const handleKeyDown = (e) => {
		if (!open) {
			if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
				e.preventDefault();
				setOpen(true);
				setFocusedIdx(0);
			}
			return;
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setFocusedIdx(i => Math.min(i + 1, allCountryCodes.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setFocusedIdx(i => Math.max(i - 1, 0));
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (focusedIdx >= 0) {
					onCountryChange(allCountryCodes[focusedIdx]);
					closeDropdown(true);
				}
				break;
			case 'Escape':
				e.preventDefault();
				closeDropdown(true);
				break;
			case 'Tab':
				closeDropdown();
				break;
		}
	};

	return (
		<Field error={error}>
			<div ref={containerRef} className="relative flex items-stretch">
				<button
					ref={btnRef} type="button"
					role="combobox" aria-expanded={open} aria-haspopup="listbox"
					onClick={() => {
						setOpen(v => !v);
						setFocusedIdx(open ? -1 : 0);
					}}
					onKeyDown={handleKeyDown}
					className="flex items-center gap-1.5 px-3 border border-r-0 rounded-l-xl bg-gray-50 hover:bg-gray-100 transition-colors shrink-0"
					style={{borderColor: error ? '#FCA5A5' : '#E5E7EB'}}>
					<img src={countryCode?.countryFlagSrc || allCountryCodes[0].countryFlagSrc} alt="flag"
					     className="w-4 h-3 rounded-sm object-cover"/>
					<span className="font-custom-bold text-[0.72rem] text-gray-700">
						{countryCode?.dialingCode || DEFAULT_COUNTRY.countryDialingCode}
					</span>
					<ChevronDownIcon
						className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}/>
				</button>

				<input type="number"
				       className={`flex-1 px-3.5 py-2.5 text-sm rounded-r-xl border outline-none transition-all duration-200 font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
					       ${error ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10'}`}
				       placeholder={placeholder} value={value}
				       onChange={e => onChange(e.target.value)}/>


				{showDrop && ReactDOM.createPortal(
					<div
						style={{
							position: 'fixed',
							top: dropPos.top,
							left: dropPos.left,
							width: dropPos.width,
							zIndex: 9999
						}}
						className={`${dropClosing ? 'dropdown-close' : 'dropdown-open'} overflow-hidden bg-white rounded-xl shadow-xl border border-gray-100`}>
						<div ref={listRef} role="listbox" onWheel={e => e.stopPropagation()}
						     className="overflow-y-auto max-h-32">
							{allCountryCodes.map((c, i) => (
								<button key={i} type="button" role="option"
								        aria-selected={countryCode?.charCode === c.countryCode}
								        className={`w-full flex items-center gap-2 px-3 py-2.5 font-custom-medium text-[0.75rem] transition-colors
								        ${focusedIdx === i ? 'bg-orange-50 text-[#D26A53]' : 'text-gray-700 hover:bg-gray-50'}`}
								        onMouseEnter={() => setFocusedIdx(i)}
								        onClick={() => {
									        onCountryChange(c);
									        closeDropdown(true);
								        }}>
									<img src={c.countryFlagSrc} alt={c.countryCode}
									     className="w-4 h-3 rounded-sm object-cover"/>
									{c.countryName} ({c.countryDialingCode})
								</button>
							))}
						</div>
					</div>,
					document.body
				)}
			</div>
		</Field>
	);
};


// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const FindMyProfilePopup = ({onClose}) => {
	const {setAccount, setAccessToken, setRedirectTo} = useAuth();

	// ── Welcome Popup ──────────────────────────────────────────────
	const welcomeRafRef = useRef(null);
	const [showWelcomePopup, setShowWelcomePopup] = useState(true);
	const [welcomeVisible, setWelcomeVisible] = useState(false);

	// Trigger animation on mount
	useEffect(() => {
		cancelAnimationFrame(welcomeRafRef.current);
		welcomeRafRef.current = requestAnimationFrame(() => {
			welcomeRafRef.current = requestAnimationFrame(() => setWelcomeVisible(true));
		});
		return () => cancelAnimationFrame(welcomeRafRef.current);
	}, []);

	const closeWelcomePopup = () => {
		setWelcomeVisible(false);
		setTimeout(() => setShowWelcomePopup(false), 220);
	};

	// ── Step ─────────────────────────────────────────────────────────────────
	const panelRef = useRef(null);

	// ── Step ─────────────────────────────────────────────────────────────────
	const [step, setStep] = useState(STEPS.SEARCH);
	const stepDirectionRef = useRef(1); // 1 = forward, -1 = backward

	// ── Loading ───────────────────────────────────────────────────────────────
	const [isSearching, setIsSearching] = useState(false);
	const [isSendingOtp, setIsSendingOtp] = useState(false);
	const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// ── Errors ────────────────────────────────────────────────────────────────
	const [errors, setErrors] = useState({});

	const isValidEmail = (val) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(val);

	const [phone, setPhone] = useState(makePhoneState());
	const [email, setEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [middleName, setMiddleName] = useState("");
	const [familyTreeName, setFamilyTreeName] = useState("");
	const [ftQuery, setFtQuery] = useState("");
	const [ftOpen, setFtOpen] = useState(false);
	const [ftFocusedIdx, setFtFocusedIdx] = useState(-1);
	const ftContainerRef = useRef(null);
	const ftListRef = useRef(null);

	const filteredFt = familyTreeNames
		.filter(n => n.toLowerCase().includes(ftQuery.toLowerCase()))
		.slice(0, 25);

	// ── Results ───────────────────────────────────────────────────────────────
	const [results, setResults] = useState([]);
	const [selectedProfile, setSelectedProfile] = useState(null);

	// ── OTP ───────────────────────────────────────────────────────────────────
	const [otp, setOtp] = useState("");
	const [resendTimer, setResendTimer] = useState(0);
	const timerRef = useRef(null);
	const otpInputRef = useRef(null);

	// ── Claim ─────────────────────────────────────────────────────────────────
	const [claimPhone, setClaimPhone] = useState(makePhoneState());
	const [claimEmail, setClaimEmail] = useState("");

	// ── Success ───────────────────────────────────────────────────────────────
	const [successType, setSuccessType] = useState(""); // "login" | "claim"

	// ── Success Step Transition ───────────────────────────────────────────────────────────────
	useEffect(() => {
		if (!panelRef.current) return;

		if (step === STEPS.SUCCESS) {
			const el = panelRef.current;
			const from = el.offsetHeight;

			// Lock current height so transition has a defined start point
			el.style.transition = 'none';
			el.style.height = `${from}px`;

			// Force reflow so browser registers the start value
			void el.offsetHeight;

			// Now animate to target
			requestAnimationFrame(() => {
				el.style.transition = 'height 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
				el.style.height = '25rem';
			});
		} else {
			// Reset when leaving SUCCESS
			const el = panelRef.current;
			el.style.transition = '';
			el.style.height = '';
		}
	}, [step]);

	// ── Cleanup ───────────────────────────────────────────────────────────────
	useEffect(() => () => clearInterval(timerRef.current), []);

	// ── Family tree keyboard nav ──────────────────────────────────────────────
	useEffect(() => {
		if (ftFocusedIdx >= 0 && ftListRef.current)
			ftListRef.current.children[ftFocusedIdx]?.scrollIntoView({block: "nearest"});
	}, [ftFocusedIdx]);

	useEffect(() => {
		const handler = (e) => {
			if (ftContainerRef.current && !ftContainerRef.current.contains(e.target))
				setFtOpen(false);
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const handleFtKeyDown = (e) => {
		if (!ftOpen) {
			if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
				e.preventDefault();
				setFtOpen(true);
				setFtFocusedIdx(0);
			}
			return;
		}
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setFtFocusedIdx(i => Math.min(i + 1, filteredFt.length - 1));
				break;
			case "ArrowUp":
				e.preventDefault();
				setFtFocusedIdx(i => Math.max(i - 1, 0));
				break;
			case "Enter":
			case " ":
				e.preventDefault();
				if (ftFocusedIdx >= 0) {
					const picked = filteredFt[ftFocusedIdx];
					setFamilyTreeName(picked);
					setFtQuery(picked);
					setFtOpen(false);
					clearErr("familyTreeName");
				}
				break;
			case "Escape":
				e.preventDefault();
				setFtOpen(false);
				break;
			case "Tab":
				setFtOpen(false);
				break;
		}
	};

	// ── Resend timer ──────────────────────────────────────────────────────────
	const startResendTimer = () => {
		setResendTimer(RESEND_COOLDOWN);
		clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			setResendTimer(prev => {
				if (prev <= 1) {
					clearInterval(timerRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	// ── Helpers ───────────────────────────────────────────────────────────────
	const clearErr = (f) => setErrors(p => ({...p, [f]: ""}));
	const goTo = (nextStep, dir = 1) => {
		stepDirectionRef.current = dir;
		setStep(nextStep);
	};
	const isLoading = isSearching || isSendingOtp || isVerifyingOtp || isSubmitting;

	// ── Validate ──────────────────────────────────────────────────────────────
	const validateSearch = () => {
		const errs = {};
		if (!familyTreeName) errs.familyTreeName = tf("ERRORS.FAMILY_TREE_REQUIRED");
		if (!phone.number) errs.phone = tf("ERRORS.PHONE_REQUIRED");
		if (!email) errs.email = tf("ERRORS.EMAIL_REQUIRED");
		else if (!isValidEmail(email)) errs.email = tf("ERRORS.EMAIL_INVALID");
		if (!firstName) errs.firstName = tf("ERRORS.FIRST_NAME_REQUIRED");
		if (!middleName) errs.middleName = tf("ERRORS.MIDDLE_NAME_REQUIRED");
		setErrors(errs);
		return Object.keys(errs).length === 0;
	};

	// ── Action handlers ───────────────────────────────────────────────────────

	const handleSearch = async () => {
		if (!validateSearch()) return;
		setIsSearching(true);
		try {
			const res = await findProfiles({
				phoneNumber: phone.number || undefined,
				dialingCode: phone.number ? phone.dialingCode : undefined,
				email,
				firstName,
				middleName,
				familyTreeName,
			});
			if (res.success) {
				setResults(res.data);
				goTo(STEPS.RESULTS, 1);
			} else toast.error(res.message ?? tf("ERRORS.SEARCH_FAILED"));
		} catch {
			toast.error(tf("ERRORS.SEARCH_FAILED"));
		} finally {
			setIsSearching(false);
		}
	};

	const handleSendOtp = async (profile) => {
		setIsSendingOtp(true);
		try {
			const result = await sendProfileClaimOtp({accountId: profile._id});
			if (result?.success) {
				goTo(STEPS.OTP, 1);
				startResendTimer();
				toast.success(result.message ?? tf("OTP.SENT_SUCCESS"));
				setTimeout(() => otpInputRef.current?.focus(), 350);
			} else {
				toast.error(result?.message ?? tf("ERRORS.OTP_SEND_FAILED"));
			}
		} catch {
			toast.error(tf("ERRORS.OTP_SEND_FAILED"));
		} finally {
			setIsSendingOtp(false);
		}
	};

	const handleSelectProfile = async (profile) => {
		setSelectedProfile(profile);
		const canSendOtp = (profile.matchConfidence === "phone" || profile.matchConfidence === "email") && profile.hasPhone;
		if (canSendOtp) await handleSendOtp(profile);
		else goTo(STEPS.CLAIM, 1);
	};

	const handleVerifyOtp = async () => {
		if (otp.length !== 6) {
			setErrors(p => ({...p, otp: tf("ERRORS.OTP_LENGTH")}));
			return;
		}
		setIsVerifyingOtp(true);
		try {
			const res = await verifyProfileClaimOtp({accountId: selectedProfile._id, otp});
			if (res.success) {
				// navigate("/personal-profile", {replace: true});
				setAccount(res.data.user);
				setAccessToken(res.data.accessToken);
				setRedirectTo("/personal-profile");
				setSuccessType("login");
				goTo(STEPS.SUCCESS, 1);
			} else {
				toast.error(res.message ?? tf("ERRORS.VERIFY_FAILED"));
				setErrors(p => ({...p, otp: res.message ?? tf("ERRORS.VERIFY_FAILED")}));
			}
		} catch {
			toast.error(tf("ERRORS.VERIFY_FAILED"));
		} finally {
			setIsVerifyingOtp(false);
		}
	};

	const handleSubmitClaim = async () => {
		const errs = {};

		if (!claimPhone.number && !claimEmail) {
			errs.claimPhone = tf("ERRORS.CONTACT_REQUIRED");
		}
		if (claimEmail && !isValidEmail(claimEmail)) {
			errs.claimEmail = tf("ERRORS.EMAIL_INVALID");
		}

		// Set errors AND return early if any exist — don't proceed to API call
		if (Object.keys(errs).length > 0) {
			setErrors(p => ({...p, ...errs}));
			return;  // ← this line is the key fix
		}

		setIsSubmitting(true);
		try {
			const noContact = selectedProfile && !selectedProfile.hasPhone && !selectedProfile.hasEmail;
			const noMatch = results.length === 0 && !selectedProfile;
			const res = await submitProfileClaimRequest({
				submittedPhone: claimPhone.number
					? `${claimPhone.dialingCode}${claimPhone.number}`
					: undefined,
				submittedEmail: claimEmail || undefined,
				submittedFirstName: firstName || undefined,
				submittedMiddleName: middleName || undefined,
				submittedFamilyTreeName: familyTreeName,
				matchedAccountId: selectedProfile?._id ?? null,
				matchConfidence: selectedProfile?.matchConfidence ?? null,
				status: noMatch ? "NO_MATCH" : noContact ? "NO_CONTACT" : "PENDING",
			});
			if (res.success) {
				setSuccessType("claim");
				goTo(STEPS.SUCCESS, 1);
			} else toast.error(res.message ?? tf("ERRORS.SUBMIT_FAILED"));
		} catch (error) {
			toast.error(error.response.data.message ?? tf("ERRORS.SUBMIT_FAILED"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlePrimaryAction = () => {
		if (step === STEPS.SEARCH) return handleSearch();
		if (step === STEPS.RESULTS) {
			setSelectedProfile(null);
			goTo(STEPS.CLAIM, 1);
		}
		if (step === STEPS.OTP) return handleVerifyOtp();
		if (step === STEPS.CLAIM) return handleSubmitClaim();
	};

	const primaryLabel = () => {
		if (isLoading) return tf("FOOTER.PLEASE_WAIT");
		if (step === STEPS.SEARCH) return tf("FOOTER.SEARCH");
		if (step === STEPS.RESULTS) return tf("FOOTER.NONE_OF_THESE");
		if (step === STEPS.OTP) return tf("FOOTER.VERIFY_LOGIN");
		if (step === STEPS.CLAIM) return tf("FOOTER.SUBMIT_REQUEST");
		return "";
	};

	const stepAnimClass = stepDirectionRef.current === 1 ? "step-forward" : "step-backward";

	// ─────────────────────────────────────────────────────────────────────────
	// RENDER
	// ─────────────────────────────────────────────────────────────────────────

	return (
		<>
			{/* ── Main Panel ── */}
			{/*<div*/}
			{/*	className={`form-modal-container bg-white w-full sm:max-w-md*/}
			{/*		flex flex-col overflow-hidden relative*/}
			{/*		transition-all duration-500 ease-in-out*/}
			{/*		${step === STEPS.SUCCESS ? "!h-auto max-h-[25rem]" : "max-h-[100dvh] sm:max-h-[90dvh]"}*/}
			{/*	`}*/}
			{/*	style={{boxShadow: '0 32px 64px rgba(0,0,0,0.18)'}}*/}
			{/*>*/}
			<div
				ref={panelRef}
				className="form-modal-container bg-white w-full sm:max-w-md
                    flex flex-col overflow-hidden relative"
				style={{boxShadow: '0 32px 64px rgba(0,0,0,0.18)'}}
			>
				{/* ── Header ── */}
				<div className="shrink-0 px-5 pt-5 pb-4 bg-white z-20"
				     style={{borderBottom: "1px solid #F3F3F3"}}>
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0 pr-3">
							<div className="flex items-center gap-2 mb-0.5">
								<div className="w-2 h-2 rounded-full shrink-0" style={{background: "#A20000"}}/>
								<p className="font-custom-bold text-[0.6rem] text-gray-400 uppercase tracking-widest">
									{t("HEADER.JOGLEKARKUL")}
								</p>
							</div>
							<h2 className="font-custom-extra-bold text-base text-gray-900 tracking-tight">
								{tf("HEADER.TITLE")}
							</h2>
							<p className="font-custom-regular text-[0.72rem] text-gray-500 mt-0.5">
								{step === STEPS.SEARCH && tf("HEADER.SUBTITLE.SEARCH")}
								{step === STEPS.RESULTS && (results.length > 0 ? tf("HEADER.SUBTITLE.RESULTS") : tf("HEADER.SUBTITLE.RESULTS_NONE"))}
								{step === STEPS.OTP && tf("HEADER.SUBTITLE.OTP")}
								{step === STEPS.CLAIM && tf("HEADER.SUBTITLE.CLAIM")}
								{step === STEPS.SUCCESS && (successType === "login" ? tf("HEADER.SUBTITLE.SUCCESS_LOGIN") : tf("HEADER.SUBTITLE.SUCCESS_CLAIM"))}
							</p>
						</div>
						<div className="flex items-center gap-3 shrink-0">
							<img src="/other_imgs/logo-joglekar kul-72x72.png" className="h-9 w-9"
							     alt="JoglekarKul"/>
							<button
								type="button"
								onClick={() => onClose()} // or navigate("/home")
								className="w-8 h-8 flex items-center justify-center rounded-full
                                    transition-colors hover:bg-gray-100"
								style={{border: "1.5px solid #E5E7EB"}}
								aria-label="Close"
							>
								<X className="w-4 h-4 text-gray-500"/>
							</button>
						</div>
					</div>
					{step !== STEPS.SUCCESS && <StepIndicator currentStep={step}/>}
				</div>

				{/* ── Body ── */}
				<div
					key={step}
					className={`flex-1 overflow-y-auto px-6 py-5 
						thin-scrollbar ${step !== STEPS.SUCCESS ? stepAnimClass : ""}
					`}
				>

					{/* ════ SEARCH ════ */}
					{step === STEPS.SEARCH && (
						<div className="flex flex-col gap-4">
							{/* Family Tree dropdown */}
							<Field error={errors.familyTreeName}>
								<InputLabel required>{tf("SEARCH.FAMILY_TREE_NAME.LABEL")}</InputLabel>
								<div ref={ftContainerRef} className="relative">
									<div className="relative">
										<input
											type="text" autoComplete="off"
											placeholder={tf("SEARCH.FAMILY_TREE_NAME.PLACEHOLDER")}
											value={ftQuery}
											onChange={e => {
												setFtQuery(e.target.value);
												setFamilyTreeName("");
												setFtOpen(true);
												setFtFocusedIdx(0);
												clearErr("familyTreeName");
											}}
											onFocus={() => setFtOpen(true)}
											onKeyDown={handleFtKeyDown}
											className={inputCls(!!errors.familyTreeName)}
											style={familyTreeName
												? {
													borderColor: "#D26A53",
													boxShadow: "0 0 0 2px rgba(210,106,83,0.10)"
												}
												: {}}
										/>
										{familyTreeName ? (
											<div className="absolute right-3 top-1/2 -translate-y-1/2
                                            w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
												<Check className="w-3 h-3 text-white" strokeWidth={3}/>
											</div>
										) : (
											<ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2
                                            w-4 h-4 text-gray-400 transition-transform duration-200
                                            ${ftOpen ? "rotate-180" : ""}`}/>
										)}
									</div>
									{ftOpen && filteredFt.length > 0 && (
										<div className="absolute top-full left-0 right-0 mt-1 bg-white
                                        rounded-xl border border-gray-100 shadow-xl z-10 overflow-hidden">
											<div ref={ftListRef} role="listbox"
											     className="overflow-y-auto max-h-36 thin-scrollbar">
												{filteredFt.map((name, i) => (
													<button key={name} type="button" role="option"
													        className="w-full text-left px-3.5 py-2.5 font-custom-medium
                                                        text-[0.78rem] transition-colors"
													        style={{
														        background: ftFocusedIdx === i
															        ? "rgba(210,106,83,0.06)"
															        : familyTreeName === name
																        ? "rgba(210,106,83,0.04)"
																        : "transparent",
														        color: ftFocusedIdx === i || familyTreeName === name
															        ? "#D26A53" : "#374151",
													        }}
													        onMouseEnter={() => setFtFocusedIdx(i)}
													        onMouseDown={e => e.preventDefault()}
													        onClick={() => {
														        setFamilyTreeName(name);
														        setFtQuery(name);
														        setFtOpen(false);
														        clearErr("familyTreeName");
													        }}
													>
														{name}
													</button>
												))}
											</div>
										</div>
									)}
								</div>
							</Field>

							<div className="flex items-center gap-2 text-[0.68rem] text-gray-400">
								<div className="flex-1 h-px bg-gray-200"/>
								<span className="font-custom-medium shrink-0">{tf("SEARCH.DIVIDER_CONTACT")}</span>
								<div className="flex-1 h-px bg-gray-200"/>
							</div>

							<Field>
								<InputLabel required>{tf("SEARCH.PHONE.LABEL")}</InputLabel>
								<PhoneRow
									value={phone.number}
									onChange={v => {
										setPhone(p => ({...p, number: v}));
										clearErr("phone");
									}}
									countryCode={phone}
									onCountryChange={c => setPhone({
										charCode: c.countryCode,
										dialingCode: c.countryDialingCode,
										countryFlagSrc: c.countryFlagSrc,
										countryName: c.countryName,
										number: phone.number,
									})}
									error={errors.phone}
									placeholder={tf("SEARCH.PHONE.PLACEHOLDER")}
									allCountryCodes={countryCodes}
								/>
							</Field>

							<Field error={errors.email}>
								<InputLabel htmlFor="fmp-email" required>{tf("SEARCH.EMAIL.LABEL")}</InputLabel>
								<input id="fmp-email" type="email"
								       placeholder={tf("SEARCH.EMAIL.PLACEHOLDER")} value={email}
								       onChange={e => {
									       setEmail(e.target.value);
									       clearErr("email");
								       }}
								       className={inputCls(!!errors.email)}/>
							</Field>

							<div className="flex items-center gap-2 text-[0.68rem] text-gray-400">
								<div className="flex-1 h-px bg-gray-200"/>
								<span className="font-custom-medium shrink-0">{tf("SEARCH.DIVIDER_NAME")}</span>
								<div className="flex-1 h-px bg-gray-200"/>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<Field error={errors.firstName}>
									<InputLabel htmlFor="fmp-first"
									            required>{tf("SEARCH.FIRST_NAME.LABEL")}</InputLabel>
									<input id="fmp-first" type="text"
									       placeholder={tf("SEARCH.FIRST_NAME.PLACEHOLDER")} value={firstName}
									       onChange={e => {
										       setFirstName(e.target.value);
										       clearErr("firstName");
									       }}
									       className={inputCls(!!errors.firstName)}/>
								</Field>
								<Field error={errors.middleName}>
									<InputLabel htmlFor="fmp-middle"
									            required>{tf("SEARCH.MIDDLE_NAME.LABEL")}</InputLabel>
									<input id="fmp-middle" type="text"
									       placeholder={tf("SEARCH.MIDDLE_NAME.PLACEHOLDER")} value={middleName}
									       onChange={e => {
										       setMiddleName(e.target.value);
										       clearErr("middleName");
									       }}
									       className={inputCls(!!errors.middleName)}/>
								</Field>
							</div>
						</div>
					)}

					{/* ════ RESULTS ════ */}
					{step === STEPS.RESULTS && (
						<div className="flex flex-col gap-3">
							{results.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-8 text-center">
									<div
										className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
										<UserSearch className="w-7 h-7 text-gray-400"/>
									</div>
									<div>
										<p className="font-custom-bold text-sm text-gray-700 mb-1">
											{tf("RESULTS.NO_RESULTS_TITLE")}
										</p>
										<p className="font-custom-regular text-[0.72rem] text-gray-400 leading-relaxed">
											{tf("RESULTS.NO_RESULTS_SUBTITLE")}
										</p>
									</div>
								</div>
							) : (
								results.map(profile => (
									<ProfileCard key={profile._id} profile={profile}
									             onSelect={handleSelectProfile}/>
								))
							)}
						</div>
					)}

					{/* ════ OTP ════ */}
					{step === STEPS.OTP && (
						<div className="flex flex-col gap-5">
							<div className="flex gap-3 items-start p-3.5 rounded-xl"
							     style={{background: "#EFF6FF", border: "1px solid #BFDBFE"}}>
								<ShieldCheck className="w-5 h-5 text-blue-500 shrink-0 mt-0.5"/>
								<p className="font-custom-medium text-[0.75rem] text-blue-800 leading-relaxed">
									{tf("OTP.INFO_MESSAGE")}
								</p>
							</div>

							{selectedProfile && (
								<div className="flex items-center gap-3 p-3 rounded-xl"
								     style={{background: "#FAFAFA", border: "1.5px solid #E5E7EB"}}>
									<ProfileAvatar profile={selectedProfile} size="h-9 w-9"/>
									<div className="flex-1 min-w-0">
										<p className="font-custom-bold text-sm text-gray-800 truncate">
											{[selectedProfile.firstName, selectedProfile.middleName, selectedProfile.lastName]
												.filter(Boolean).join(" ")}
										</p>
										<p className="font-custom-medium text-[0.68rem] text-gray-500 truncate">
											{selectedProfile.familyTreeName}
										</p>
									</div>
								</div>
							)}

							<Field error={errors.otp}>
								<InputLabel htmlFor="fmp-otp">{tf("OTP.LABEL")}</InputLabel>
								<input
									ref={otpInputRef}
									id="fmp-otp" type="text" inputMode="numeric"
									maxLength={6} placeholder={tf("OTP.PLACEHOLDER")} value={otp}
									onChange={e => {
										setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
										clearErr("otp");
									}}
									className="w-full px-3.5 py-3 border rounded-xl outline-none
                                        transition-all duration-200 font-custom-mono-bold bg-white
                                        text-gray-800 placeholder-gray-300 text-center tracking-[0.65rem] text-xl"
									style={errors.otp
										? {borderColor: "#FCA5A5", boxShadow: "0 0 0 2px rgba(252,165,165,0.2)"}
										: {borderColor: "#E5E7EB"}}
									onFocus={e => {
										if (!errors.otp) e.target.style.borderColor = "#D26A53";
									}}
									onBlur={e => {
										if (!errors.otp) e.target.style.borderColor = "#E5E7EB";
									}}
								/>
							</Field>

							<div className="text-center">
								{resendTimer > 0 ? (
									<p className="font-custom-medium text-[0.72rem] text-gray-400">
										{tf("OTP.RESEND")} ({resendTimer}s)
									</p>
								) : (
									<button type="button" disabled={isSendingOtp}
									        onClick={() => selectedProfile && handleSendOtp(selectedProfile)}
									        className="font-custom-semi-bold text-[0.72rem] text-[#D26A53]
                                            hover:underline disabled:opacity-50 transition-opacity">
										{isSendingOtp ? tf("OTP.RESENDING") : tf("OTP.RESEND")}
									</button>
								)}
							</div>
						</div>
					)}

					{/* ════ CLAIM ════ */}
					{step === STEPS.CLAIM && (
						<div className="flex flex-col gap-4">
							<div className="flex gap-3 items-start p-3.5 rounded-xl"
							     style={{background: "#FFFBEB", border: "1px solid #FDE68A"}}>
								<svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none"
								     viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round"
									      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
								</svg>
								<p className="font-custom-medium text-[0.75rem] text-amber-800 leading-relaxed">
									{!selectedProfile
										? tf("CLAIM.INFO_NO_MATCH")
										: (!selectedProfile.hasPhone && !selectedProfile.hasEmail)
											? tf("CLAIM.INFO_NO_CONTACT")
											: tf("CLAIM.INFO_MANUAL")}
								</p>
							</div>

							{selectedProfile && (
								<div className="flex items-center gap-3 p-3 rounded-xl"
								     style={{background: "#FAFAFA", border: "1.5px solid #E5E7EB"}}>
									<ProfileAvatar profile={selectedProfile} size="h-9 w-9"/>
									<div className="flex-1 min-w-0">
										<p className="font-custom-bold text-sm text-gray-800 truncate">
											{[selectedProfile.firstName, selectedProfile.middleName, selectedProfile.lastName]
												.filter(Boolean).join(" ")}
										</p>
										<p className="font-custom-medium text-[0.68rem] text-gray-500 truncate">
											{selectedProfile.familyTreeName}
										</p>
									</div>
								</div>
							)}

							<Field>
								<InputLabel htmlFor="fmp-claim-phone"
								            required>{tf("CLAIM.PHONE.LABEL")}</InputLabel>
								<PhoneRow
									value={claimPhone.number}
									onChange={v => {
										setClaimPhone(p => ({...p, number: v}));
										clearErr("claimPhone");
									}}
									countryCode={claimPhone}
									onCountryChange={c => setClaimPhone({
										charCode: c.countryCode,
										dialingCode: c.countryDialingCode,
										countryFlagSrc: c.countryFlagSrc,
										countryName: c.countryName,
										number: claimPhone.number,
									})}
									error={errors.claimPhone}
									placeholder={tf("CLAIM.PHONE.PLACEHOLDER")}
									allCountryCodes={countryCodes}
								/>
							</Field>

							<Field error={errors.claimEmail}>
								<InputLabel htmlFor="fmp-claim-email">
									{tf("CLAIM.EMAIL.LABEL")}
								</InputLabel>
								<input
									id="fmp-claim-email" type="email"
									placeholder={tf("CLAIM.EMAIL.PLACEHOLDER")} value={claimEmail}
									onChange={e => setClaimEmail(e.target.value)}
									className={inputCls(false)}
								/>
							</Field>
						</div>
					)}

					{/* ════ SUCCESS ════ */}
					{step === STEPS.SUCCESS && (
						<div className="flex flex-col items-center gap-5 py-8 text-center">
							<div className="w-20 h-20 rounded-full flex items-center justify-center"
							     style={{background: successType === "login" ? "#DCFCE7" : "#FDF0ED"}}>
								{successType === "login"
									? <Check className="w-10 h-10 text-emerald-600" strokeWidth={2.5}/>
									: <ShieldCheck className="w-10 h-10" style={{color: "#D26A53"}}/>
								}
							</div>
							<div>
								<p className="font-custom-extra-bold text-xl text-gray-900 mb-2">
									{successType === "login" ? tf("SUCCESS.LOGIN.TITLE") : tf("SUCCESS.CLAIM.TITLE")}
								</p>
								<p className="font-custom-regular text-[0.78rem] text-gray-500 leading-relaxed max-w-[260px] mx-auto">
									{successType === "login" ? tf("SUCCESS.LOGIN.SUBTITLE") : tf("SUCCESS.CLAIM.SUBTITLE")}
								</p>
							</div>
							{successType === "login" && (
								<div
									className="flex items-center gap-2 text-[0.72rem] text-gray-400 font-custom-medium">
									<Loader2 className="w-4 h-4 animate-spin"/> Redirecting…
								</div>
							)}
						</div>
					)}
				</div>

				{/* ── Footer ── */}
				{step !== STEPS.SUCCESS && (
					<div
						className="shrink-0 px-5 py-4 bg-white z-20"
						style={{borderTop: "1px solid #F3F3F3"}}
					>
						<div className="flex items-center justify-between gap-3">

							{/* Left: Back or spacer */}
							{(step === STEPS.RESULTS || step === STEPS.OTP || step === STEPS.CLAIM) ? (
								<button type="button" onClick={() => goTo(
									step === STEPS.RESULTS ? STEPS.SEARCH : STEPS.RESULTS, -1)}
								        disabled={isLoading}
								        className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-600
                                        transition-all duration-150 hover:bg-gray-100 disabled:opacity-50"
								        style={{border: "1.5px solid #E5E7EB"}}>
									← {tf("FOOTER.BACK")}
								</button>
							) : <div/>}

							{/* Progress dots (matches YellowPagesHomePage footer exactly) */}
							<div className="flex items-center gap-1.5">
								{STEP_ORDER.map((s) => {
									const idx = STEP_ORDER.indexOf(s);
									const cur = STEP_ORDER.indexOf(step);
									return (
										<div key={s} className="rounded-full transition-all duration-300"
										     style={{
											     width: step === s ? "20px" : "6px",
											     height: "6px",
											     background: step === s ? "#A20000"
												     : idx < cur ? "#F5CEC7"
													     : "#E5E7EB",
										     }}/>
									);
								})}
							</div>

							{/* Primary action */}
							<button type="button" onClick={handlePrimaryAction} disabled={isLoading}
							        className="flex items-center gap-2 px-6 py-2.5 rounded-xl transform-gpu
                                    font-custom-bold text-sm text-white transition-all duration-150
                                    hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
							        style={{
								        background: "linear-gradient(135deg, #A20000, #8a0000)",
								        boxShadow: "0 4px 14px rgba(162,0,0,0.30)",
							        }}>
								{isLoading ? (
									<Loader2 className="w-4 h-4 animate-spin shrink-0"/>
								) : step === STEPS.SEARCH ? (
									<Search className="w-3.5 h-3.5 shrink-0"/>
								) : step !== STEPS.CLAIM ? (
									<ArrowRight className="w-3.5 h-3.5 shrink-0"/>
								) : null}
								{primaryLabel()}
							</button>
						</div>
					</div>
				)}
			</div>

			{/* ── Welcome Popup ── */}
			{showWelcomePopup && (
				<div
					className="fixed inset-0 z-[210] flex items-center justify-center p-6
                        transition-opacity duration-220 rounded-2xl"
					style={{
						background: "rgba(0,0,0,0.6)",
						backdropFilter: "blur(4px)",
						opacity: welcomeVisible ? 1 : 0,
					}}
				>
					<div
						className="bg-white rounded-2xl w-full max-w-sm flex flex-col overflow-hidden
                            transition-all duration-220"
						style={{
							boxShadow: "0 32px 64px rgba(0,0,0,0.22)",
							transform: welcomeVisible
								? "scale(1) translateY(0)"
								: "scale(0.96) translateY(8px)",
							opacity: welcomeVisible ? 1 : 0,
						}}
					>
						{/* Header */}
						<div className="shrink-0 px-6 pt-6 pb-4 bg-white"
						     style={{borderBottom: "1px solid #F3F3F3"}}>
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="w-16 h-16 rounded-full overflow-hidden shadow-md">
									<img
										src="/other_imgs/logo-joglekar kul-72x72.png"
										alt="JoglekarKul"
										className="w-full h-full object-cover"
									/>
								</div>
								<div>
									<h2 className="font-custom-extra-bold text-lg text-gray-900 tracking-tight">
										{tf("WELCOME_POPUP.TITLE")}
									</h2>
									<p className="font-custom-regular text-[0.78rem] text-gray-500 mt-1.5 leading-relaxed">
										{tf("WELCOME_POPUP.MESSAGE")}
									</p>
								</div>
							</div>
						</div>

						{/* Footer — two actions */}
						<div className="shrink-0 px-6 py-5 flex flex-col gap-3">
							{/* Primary CTA */}
							<button
								type="button"
								onClick={closeWelcomePopup}
								className="w-full flex items-center justify-center gap-2 px-6 py-3
			                        rounded-xl transform-gpu font-custom-bold text-sm text-white
			                        transition-all duration-150 hover:opacity-90 active:scale-95"
								style={{
									background: "linear-gradient(135deg, #A20000, #8a0000)",
									boxShadow: "0 4px 14px rgba(162,0,0,0.30)",
								}}
							>
								{tf("WELCOME_POPUP.CTA_FIND")}
								<ArrowRight className="w-4 h-4 shrink-0"/>
							</button>

							{/* Secondary — not a Granth member */}
							<button
								type="button"
								onClick={() => {
									closeWelcomePopup();
									// Small delay so welcome popup exit animation plays
									setTimeout(() => {
										onClose();
										toast.info(tf("WELCOME_POPUP.TOAST"));
									}, 250);
								}}
								className="w-full text-center font-custom-semi-bold text-[0.78rem]
                                    text-gray-400 hover:text-gray-700 transition-colors py-1"
							>
								{tf("WELCOME_POPUP.CTA_NOT_MEMBER")}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};